/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import ProjectCard from '@/components/molecules/ProjectCard';
import ProjectControls from '../molecules/ProjectControls';
import ProjectModal from '../molecules/ProjectModal';

export default function DynamicSections({
  initialSections,
}: {
  initialSections: any[];
}) {
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Per-section filter state — each section keeps its own search/tech/sort
  const [searchQueryMap, setSearchQueryMap] = useState<Record<string, string>>(
    {},
  );
  const [selectedTechMap, setSelectedTechMap] = useState<
    Record<string, string>
  >({});
  const [sortByMap, setSortByMap] = useState<Record<string, string>>({});
  const [directionMap, setDirectionMap] = useState<
    Record<string, 'left' | 'right'>
  >({});

  const processedSections = useMemo(() => {
    return initialSections.map((section) => ({
      ...section,
      items: section.items.map((item: any) => ({
        ...item,
        tech:
          typeof item.tags === 'string'
            ? JSON.parse(item.tags || '[]')
            : item.tags || [],
        keyFeatures: item.details
          ? typeof item.details === 'string'
            ? JSON.parse(item.details).keyFeatures
            : item.details.keyFeatures || []
          : [],
        problemStatement: item.details
          ? typeof item.details === 'string'
            ? JSON.parse(item.details).problemStatement
            : item.details.problemStatement
          : null,
        solution: item.details
          ? typeof item.details === 'string'
            ? JSON.parse(item.details).solution
            : item.details.solution
          : null,
        impact: item.details
          ? typeof item.details === 'string'
            ? JSON.parse(item.details).impact
            : item.details.impact
          : null,
      })),
    }));
  }, [initialSections]);

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 500);
  };

  const [pageMap, setPageMap] = useState<Record<string, number>>({});

  const ITEMS_PER_PAGE = 4;

  const handlePageChange = (sectionId: string, newPage: number) => {
    const currentPage = pageMap[sectionId] || 1;
    const direction = newPage > currentPage ? 'right' : 'left';

    setDirectionMap((prev) => ({ ...prev, [sectionId]: direction }));
    setPageMap((prev) => ({ ...prev, [sectionId]: newPage }));

    // Defer scroll past the re-render so we land on the new section top
    requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  };

  return (
    <div className="flex flex-col gap-16 sm:gap-20 lg:gap-24 xl:gap-28">
      {processedSections.map((section) => {
        // Per-section filter state with defaults
        const searchQuery = searchQueryMap[section.id] || '';
        const selectedTech = selectedTechMap[section.id] || 'All';
        const sortBy = sortByMap[section.id] || 'newest';

        // Tags scoped to this section only
        const sectionTechs = (() => {
          const techs = new Set<string>();
          section.items.forEach((item: any) =>
            item.tech.forEach((t: string) => techs.add(t)),
          );
          return Array.from(techs).sort();
        })();

        // Apply filter, search, sort to every section
        let filteredItems = [...section.items];

        if (selectedTech !== 'All') {
          filteredItems = filteredItems.filter((item) =>
            item.tech.includes(selectedTech),
          );
        }

        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filteredItems = filteredItems.filter(
            (item) =>
              item.title.toLowerCase().includes(query) ||
              item.description.toLowerCase().includes(query) ||
              item.tech.some((t: string) => t.toLowerCase().includes(query)),
          );
        }

        filteredItems.sort((a, b) => {
          if (sortBy === 'alphabetical') {
            return (a.title || '').localeCompare(b.title || '');
          }
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
        });

        const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
        const currentPage = pageMap[section.id] || 1;
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const paginatedItems = filteredItems.slice(
          startIndex,
          startIndex + ITEMS_PER_PAGE,
        );

        return (
          <section
            key={section.id}
            id={section.id}
            className="w-full relative group/section overflow-x-hidden"
          >
            <div className="flex flex-col items-center lg:items-start gap-3 sm:gap-4 mb-6 sm:mb-8 animate-reveal">
              <div className="flex items-center gap-3 sm:gap-4 w-full justify-center lg:justify-start">
                <h2
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground text-center lg:text-left"
                  dangerouslySetInnerHTML={{ __html: section.title || '' }}
                  suppressHydrationWarning
                />
              </div>
              <div
                className="text-muted-foreground text-sm sm:text-base lg:text-lg text-center lg:text-left max-w-2xl"
                dangerouslySetInnerHTML={{ __html: section.description || '' }}
                suppressHydrationWarning
              />
            </div>

            {/* Render controls for any section that has items */}
            {section.items.length > 0 && (
              <ProjectControls
                searchQuery={searchQuery}
                setSearchQuery={(val) => {
                  setSearchQueryMap((prev) => ({
                    ...prev,
                    [section.id]: val,
                  }));
                  setPageMap((prev) => ({ ...prev, [section.id]: 1 }));
                }}
                selectedTech={selectedTech}
                setSelectedTech={(val) => {
                  setSelectedTechMap((prev) => ({
                    ...prev,
                    [section.id]: val,
                  }));
                  setPageMap((prev) => ({ ...prev, [section.id]: 1 }));
                }}
                sortBy={sortBy}
                setSortBy={(val) => {
                  setSortByMap((prev) => ({ ...prev, [section.id]: val }));
                  setPageMap((prev) => ({ ...prev, [section.id]: 1 }));
                }}
                availableTechs={sectionTechs}
                searchPlaceholder={`Search ${(section.title || 'items').toLowerCase()}...`}
              />
            )}

            <div
              key={`${section.id}-${currentPage}`}
              className={cn(
                'grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8',
                directionMap[section.id] === 'right'
                  ? 'animate-slide-in-right'
                  : directionMap[section.id] === 'left'
                    ? 'animate-slide-in-left'
                    : 'animate-reveal',
              )}
            >
              {filteredItems.length === 0 && (
                <div className="col-span-full py-16 flex flex-col items-center justify-center glass-dark rounded-3xl border border-white/5">
                  <p className="text-muted-foreground/50 font-bold uppercase tracking-[0.2em] text-xs">
                    No items found in this section
                  </p>
                </div>
              )}

              {paginatedItems.map((item: any) => (
                <div key={item.id} className="relative group/item">
                  <ProjectCard
                    project={item}
                    onOpenModal={() => handleOpenModal(item)}
                  />
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-between md:justify-center items-center gap-3 sm:gap-4 md:gap-6 mt-10 sm:mt-12 w-full md:w-auto md:mx-auto animate-reveal">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(section.id, currentPage - 1)}
                  className="px-4 sm:px-6 py-2 rounded-full glass border border-white/5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/5 transition-all text-xs font-bold uppercase tracking-widest cursor-pointer shrink-0"
                >
                  Prev
                </button>
                <div className="flex gap-2 justify-center flex-1 md:flex-none min-w-0 overflow-x-auto">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(section.id, i + 1)}
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-all text-xs font-bold cursor-pointer shrink-0',
                        currentPage === i + 1
                          ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                          : 'hover:bg-white/5 text-muted-foreground',
                      )}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(section.id, currentPage + 1)}
                  className="px-4 sm:px-6 py-2 rounded-full glass border border-white/5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/5 transition-all text-xs font-bold uppercase tracking-widest cursor-pointer shrink-0"
                >
                  Next
                </button>
              </div>
            )}
          </section>
        );
      })}

      {selectedItem && (
        <ProjectModal
          project={selectedItem}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
